import { Column, ColumnConfig } from './column';
import { ColumnType } from './column-type.enum';
import { DisplayColumn } from './patterns/display-column';
import { FallbackColumn, FallbackColumnConfig } from './patterns/fallback-column';

export interface ListColumnConfig extends ColumnConfig, FallbackColumnConfig {
}

export class ListColumn<T, I = any> extends Column<T> implements DisplayColumn<T>, FallbackColumn<T> {
  public readonly columnType = ColumnType.List;
  public config: ListColumnConfig = {
    cellClasses: [],
  };

  /**
   * Create a new table column that lists a number of items in a single cell. The list will be truncated if need be.
   *
   * @param title The column title, to be displayed once along the top of the table
   * @param items The list of items to display in this cell
   * @param itemConverter A converter to get the information to display for each item in the display list
   * @param extras Extra configuration for this column
   */
  constructor(
    public readonly title: string,
    public readonly items: (row: T) => I[],
    public readonly itemConverter: (item: I) => string,
    extraConfig?: ListColumnConfig,
  ) {
    super();
    this.applyConfig(extraConfig);
  }

  public toString(): string {
    return `[ListColumn (title: ${this.title})]`;
  }

  public get fallback(): string {
    return this.config.fallback ?? '(none)';
  }

  public useFallback(row: T): boolean {
    const value = this.items(row);
    return (typeof value === 'undefined' || value === null || value.length === 0);
  }

  public display(row: T): string {
    const items: I[] = this.items(row);
    const text = items.slice(0, 3)
      .map(item => this.itemConverter(item))
      .join(', ');
    return `${text}${items.length > 3 ? '...' : ''} (${items.length} total)`;
  }
}
